import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    Stack,
    Text,
    Textarea,
} from "@chakra-ui/react";
import {
    walletDialogPaperSx,
    walletTextFieldSx,
    walletCancelButtonSx,
} from "../artistWallet/dialogStyles";

const radioProps = {
    colorScheme: "teal",
    color: "rgba(255, 255, 255, 0.85)",
};

const dangerButtonProps = {
    background: "rgba(244, 67, 54, 0.15)",
    color: "rgba(229, 115, 115, 1)",
    border: "1px solid rgba(244, 67, 54, 0.4)",
    fontWeight: 600,
    _hover: { background: "rgba(244, 67, 54, 0.25)" },
    _disabled: { opacity: 0.5 },
};

/**
 * Calls the cancel endpoint and propagates success/error via toast.
 * @param {Object} args - Submission arguments
 * @returns {Promise<void>}
 */
async function submitCancel({ subscriptionId, immediate, reason, setLoading, onCancelled, onClose }) {
    setLoading(true);
    try {
        const { data } = await axios.post(
            `/admin/subscriptions/${subscriptionId}/cancel`,
            { immediate, reason: reason.trim() || undefined },
        );
        toast.success(immediate ? "Abonnement annulé" : "Annulation programmée en fin de période");
        onCancelled?.(data);
        onClose();
    } catch (error) {
        const msg = error?.response?.data || "Échec de l'annulation";
        toast.error(typeof msg === "string" ? msg : "Échec de l'annulation");
    } finally {
        setLoading(false);
    }
}

/**
 * Renders the radio choice between immediate and end-of-period cancellation.
 * @param {Object} props
 * @param {boolean} props.immediate - Current radio selection
 * @param {Function} props.setImmediate - State setter for the radio value
 * @returns {React.ReactElement}
 */
function CancelModeRadios({ immediate, setImmediate }) {
    return (
        <RadioGroup value={immediate ? "now" : "end"} onChange={(v) => setImmediate(v === "now")} mb={4}>
            <Stack spacing={2}>
                <Radio value="end" {...radioProps} fontSize="0.85rem">
                    À la fin de la période en cours (auto_renew = false)
                </Radio>
                <Radio value="now" {...radioProps} fontSize="0.85rem">
                    Immédiate (annulation Stripe + DB)
                </Radio>
            </Stack>
        </RadioGroup>
    );
}

/**
 * Resets the dialog state every time it opens, then renders the form.
 * @param {Object} props - Dialog state and setters
 * @returns {React.ReactElement}
 */
function CancelDialogBody({ open, immediate, setImmediate, reason, setReason }) {
    useEffect(() => {
        if (open) { setImmediate(false); setReason(""); }
    }, [open, setImmediate, setReason]);
    return (
        <ModalBody>
            <Text color="rgba(255, 255, 255, 0.7)" mb={4} fontSize="0.9rem">
                Choisis le mode d'annulation. La raison est tracée dans l'historique de l'abonnement.
            </Text>
            <CancelModeRadios immediate={immediate} setImmediate={setImmediate} />
            <Textarea rows={3} placeholder="Raison (optionnelle)"
                value={reason} onChange={(e) => setReason(e.target.value)} {...walletTextFieldSx} />
        </ModalBody>
    );
}

/**
 * Modal to cancel a subscription, with immediate vs end-of-period choice.
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Close callback
 * @param {string|number} props.subscriptionId - Target subscription ID
 * @param {Function} props.onCancelled - Optional success callback (receives API response)
 * @returns {React.ReactElement}
 */
function CancelSubscriptionDialog({ open, onClose, subscriptionId, onCancelled }) {
    const [immediate, setImmediate] = useState(false);
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);

    const handleConfirm = () => submitCancel({ subscriptionId, immediate, reason, setLoading, onCancelled, onClose });

    return (
        <Modal isOpen={open} onClose={onClose} size="md" isCentered>
            <ModalOverlay />
            <ModalContent {...walletDialogPaperSx}>
                <ModalHeader color="rgba(255, 255, 255, 0.95)" fontWeight={600}>
                    Annuler l'abonnement
                </ModalHeader>
                <CancelDialogBody open={open} immediate={immediate} setImmediate={setImmediate}
                    reason={reason} setReason={setReason} />
                <ModalFooter gap={2}>
                    <Button onClick={onClose} {...walletCancelButtonSx}>Retour</Button>
                    <Button onClick={handleConfirm} isDisabled={loading} {...dangerButtonProps}>
                        {loading ? "Annulation..." : (immediate ? "Annuler maintenant" : "Programmer l'annulation")}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default CancelSubscriptionDialog;
